import { useCallback, useState } from 'react';

import {
  useGroupTerminateAgreementMutation,
  TerminatedReasonEnum,
} from 'core/graphql';

/**
 * Input for the dedicated `terminateAgreement` mutation (B3).
 *
 * No PII is re-sent: the old `updateStudentWithAgreement` echo (which resent the
 * student's identity just to flip a flag) is gone. Termination is now a minimal,
 * intention-revealing call — only the agreement id plus optional metadata.
 */
export interface ITerminateAgreementInput {
  /** Agreement (roster row) id to terminate. */
  agreementId: string;
  /** Optional termination date (ISO `Date` string); backend defaults to today. */
  terminatedDate?: string;
  /** Optional reason; the backend inlines it into `comment` as a `[reason=]` tag. */
  terminatedReason?: TerminatedReasonEnum;
  /** Optional free-text audit note. */
  comment?: string;
}

/** Backend error code returned when the agreement is already terminated. */
const ALREADY_TERMINATED = 'ALREADY_TERMINATED';

/** Outcome of a termination attempt — distinguishes the idempotent repeat. */
export interface ITerminateAgreementResult {
  success: boolean;
  /** `true` when the backend reported `ALREADY_TERMINATED` (Req: distinct UX). */
  alreadyTerminated: boolean;
}

interface IUseTerminateAgreement {
  /**
   * Terminates a single agreement via the dedicated mutation. Resolves
   * `{ success, alreadyTerminated }`:
   * - `success: true` only when the payload reports `success` — the hook then
   *   invokes the optional `onSuccess` so the caller performs the atomic refresh.
   * - `alreadyTerminated: true` when the backend returned the
   *   `ALREADY_TERMINATED` error code on a repeat call, so the caller can show a
   *   specific message instead of the generic error.
   * On any other failure both flags are `false` and `onSuccess` is not called.
   */
  terminate: (
    input: ITerminateAgreementInput,
  ) => Promise<ITerminateAgreementResult>;
  /** `true` while a termination request is in flight. */
  pending: boolean;
}

/**
 * Single-shot agreement-termination hook (Req 10.1–10.3). Wraps the generated
 * `useGroupTerminateAgreementMutation` (the dedicated `terminateAgreement`
 * resolver). The backend has no side effects on contributions, deferred
 * payments, CRM, or notifications — termination is status + date only.
 */
export const useTerminateAgreement = (
  onSuccess?: () => void,
): IUseTerminateAgreement => {
  const [run] = useGroupTerminateAgreementMutation();
  const [pending, setPending] = useState(false);

  const terminate = useCallback(
    async (
      input: ITerminateAgreementInput,
    ): Promise<ITerminateAgreementResult> => {
      if (pending) return { success: false, alreadyTerminated: false };
      setPending(true);

      try {
        const result = await run({
          variables: {
            agreementId: input.agreementId,
            terminatedDate: input.terminatedDate,
            terminatedReason: input.terminatedReason,
            comment: input.comment,
          },
        });
        const payload = result.data?.terminateAgreement;
        const success = Boolean(payload?.success);
        const alreadyTerminated = Boolean(
          payload?.customErrors?.some((e) => e?.code === ALREADY_TERMINATED),
        );
        if (success) onSuccess?.();
        return { success, alreadyTerminated };
      } catch {
        return { success: false, alreadyTerminated: false };
      } finally {
        setPending(false);
      }
    },
    [pending, run, onSuccess],
  );

  return { terminate, pending };
};
